import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, LayoutZeile } from "@kundeninfo/api-client";
import { Objektliste, Objektzeile, useBeforeUnload, useBestaetigung } from "@kundeninfo/ui";
import type { Objektstatus } from "@kundeninfo/ui";
import type { VerlassenWaechter } from "../../router.js";
import { LayoutEditor } from "../../components/layout/LayoutEditor.js";
import type { LayoutEditorClient } from "../../components/layout/LayoutEditor.js";
import { Meldung, meldungAus } from "./gemeinsam.js";
import type { VerwaltungBereichTeile } from "./gemeinsam.js";
import "../Verwaltung.css";

/**
 * Der Verwaltungsbereich LAYOUT (Spec „Layout-Editor, Etappe 1", R1): links die
 * Unternehmen mit ihrem einen Layout „Standard", rechts der Editor. Die Reihenfolge der
 * Liste kommt vom Server (Schlechtestes oben: Störung, Entwurf geändert, nicht
 * freigegeben, freigegeben).
 *
 * Ein HOOK wie die übrigen Objektbereiche (siehe `VerwaltungBereichTeile`). Solange der
 * geöffnete Entwurf ungespeichert ist, fragt jede Navigation nach, und die Liste ist
 * gesperrt.
 */

export type LayoutClient = LayoutEditorClient & Pick<ApiClient, "listLayouts">;

export type VerwaltungLayoutProps = {
  aktiv: boolean;
  client: LayoutClient;
  layoutId: string | null;
  /** Das Arbeitsunternehmen aus der Kopfleiste (ME-Etappe 1, Aufgabe 7): Ohne Layout in
   *  der Adresse öffnet der Bereich SEIN Layout zuerst. `null`: Bestand (nur der Hinweis). */
  arbeitsunternehmenId: string | null;
  onOeffnen: (layoutId: string | null) => void;
  onUnternehmen: (companyId: string) => void;
  registriereVerlassenWaechter?: (frage: VerlassenWaechter) => () => void;
};

function status(z: LayoutZeile): Objektstatus {
  if (z.stoerung === "ABLAGE_NICHT_ERREICHBAR") {
    // Der Hinweis des Servers unterscheidet „antwortet mit Fehler“ von „nicht erreichbar“
    // (Nachlese LE1); ohne Hinweis der feste Wortlaut.
    return { text: z.ablageHinweis ?? "Ablage nicht erreichbar", art: "warnung" };
  }
  if (z.stoerung !== null) return { text: "Störung", art: "fehler" };
  if (z.geaendert) return { text: "Entwurf geändert", art: "warnung" };
  if (z.freigegebenAm === null) return { text: "nicht freigegeben", art: "aus" };
  return { text: "freigegeben", art: "ok" };
}

export function useVerwaltungLayout(p: VerwaltungLayoutProps): VerwaltungBereichTeile {
  const [zeilen, setZeilen] = useState<LayoutZeile[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [neuLaden, setNeuLaden] = useState(0);
  const [ungespeichert, setUngespeichert] = useState(false);

  /*
   * Das GEÖFFNETE Layout: das aus der Adresse, sonst das des Arbeitsunternehmens (ME-Etappe
   * 1, Aufgabe 7). Bewusst ohne Navigation — die Adresse bleibt `/verwaltung/layout`: Ein
   * Ersetzen der Adresse machte die Zurück-Taste zur Schleife, und auf schmalem Gerät
   * bliebe die Liste zuerst sichtbar (`objektOffen` hängt an der Adresse).
   *
   * Solange der Entwurf ungespeichert ist, hält der Bereich die Vorgabe fest: Ein Wechsel
   * des Arbeitsunternehmens (kein Navigieren, also ohne Verlassen-Wächter) risse sonst den
   * Editor samt Entwurf aus dem Baum.
   */
  const vorgabeRef = useRef<string | null>(null);
  if (!ungespeichert) {
    vorgabeRef.current = p.arbeitsunternehmenId === null ? null
      : (zeilen ?? []).find((z) => z.companyId === p.arbeitsunternehmenId)?.layoutId ?? null;
  }
  const { aktiv } = p;
  const layoutId = p.layoutId ?? vorgabeRef.current;

  const listLayouts = p.client.listLayouts;
  useEffect(() => {
    if (!aktiv) return;
    let abgebrochen = false;
    listLayouts()
      .then((a) => { if (!abgebrochen) { setZeilen(a.items); setLadeFehler(null); } })
      .catch((f: unknown) => {
        if (!abgebrochen) setLadeFehler(meldungAus(f, "Die Layouts konnten nicht geladen werden."));
      });
    return () => { abgebrochen = true; };
  }, [aktiv, listLayouts, neuLaden]);

  useEffect(() => {
    if (aktiv) return;
    setUngespeichert(false);
    setLadeFehler(null);
  }, [aktiv]);

  useBeforeUnload(ungespeichert);

  const { bestaetigen, bestaetigung } = useBestaetigung();
  const registriere = p.registriereVerlassenWaechter;
  useEffect(() => {
    if (!ungespeichert || registriere === undefined) return;
    return registriere(async () => {
      const weiter = await bestaetigen({
        titel: "Seite verlassen?",
        text: "Der Layout-Entwurf hat ungespeicherte Änderungen. Sie gehen verloren.",
        bestaetigenText: "Verlassen",
        gefaehrlich: true,
      });
      if (weiter) setUngespeichert(false);
      return weiter;
    });
  }, [ungespeichert, registriere]);

  const onZustand = useCallback((z: { ungespeichert: boolean; listeNeu: boolean }) => {
    setUngespeichert(z.ungespeichert);
    if (z.listeNeu) setNeuLaden((n) => n + 1);
  }, []);

  const liste = (
    <>
      {ladeFehler !== null && <Meldung text={ladeFehler} />}
      {ladeFehler === null && zeilen === null && (
        <p className="vw__hinweis" role="status">Lädt …</p>
      )}
      <Objektliste label="Layouts">
        {(zeilen ?? []).map((z) => (
          <Objektzeile key={z.layoutId} name={z.unternehmen} kennung={z.kuerzel}
            meta={`Layout „${z.name}“`} status={status(z)}
            {...(z.aktiv ? {} : { marken: ["stillgelegt"] })}
            gewaehlt={z.layoutId === layoutId} gesperrt={ungespeichert}
            ariaLabel={`Layout von ${z.unternehmen} öffnen`}
            onOeffnen={() => p.onOeffnen(z.layoutId)} />
        ))}
      </Objektliste>
    </>
  );

  const detail: ReactElement = layoutId === null
    ? (
      <p className="vw__hinweis">
        Links ein Unternehmen wählen — sein Beleg-Layout steht dann hier. Akzentfarbe und
        Logo werden am Unternehmen gepflegt.
      </p>
    )
    : <LayoutEditor key={layoutId} client={p.client} layoutId={layoutId}
        onUnternehmen={p.onUnternehmen} onZustand={onZustand} />;

  return { liste, detail: <>{detail}{bestaetigung}</> };
}
